import { z } from 'zod';

export const EventExtractSourceSchema = z.enum(['url', 'pdf', 'spreadsheet', 'image']);
export type EventExtractSource = z.infer<typeof EventExtractSourceSchema>;

export const EventCandidateStatusSchema = z.enum(['pending', 'accepted', 'rejected']);
export type EventCandidateStatus = z.infer<typeof EventCandidateStatusSchema>;

/** One extracted event awaiting review. */
export const HotelEventCandidateSchema = z.object({
  id: z.string().guid(),
  hotelId: z.string().guid(),
  startsOn: z.string(),
  endsOn: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  category: z.string(),
  source: EventExtractSourceSchema,
  sourceRef: z.string().nullable(),
  status: EventCandidateStatusSchema,
  createdAt: z.string().datetime(),
});
export type HotelEventCandidate = z.infer<typeof HotelEventCandidateSchema>;

/** Request body for kicking off an extraction (URL variant; file goes
 *  multipart). The URL is bounded and must be a syntactically valid URL;
 *  scheme + private-IP checks happen server-side before enqueueing. */
export const EventExtractRequestSchema = z.object({
  url: z.string().url().max(2048),
});
export type EventExtractRequest = z.infer<typeof EventExtractRequestSchema>;

/**
 * MIME types accepted for file-based extraction. The parser handles PDFs,
 * spreadsheets (xlsx/xls/csv) and images; anything else is rejected before
 * it ever touches storage or the queue.
 */
export const EVENT_EXTRACT_ALLOWED_MIME = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'application/csv',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

/** Hard cap on uploaded extraction files (15 MB). */
export const EVENT_EXTRACT_MAX_FILE_BYTES = 15 * 1024 * 1024;

export const EventExtractResultSchema = z.object({
  jobId: z.string(),
  source: EventExtractSourceSchema,
});
export type EventExtractResult = z.infer<typeof EventExtractResultSchema>;

/** Accept / reject a batch of candidates. */
export const ReviewEventCandidatesSchema = z.object({
  acceptIds: z.array(z.string().guid()).default([]),
  rejectIds: z.array(z.string().guid()).default([]),
});
export type ReviewEventCandidates = z.infer<typeof ReviewEventCandidatesSchema>;

/** The structured shape the AI / parsers must produce per event. */
export const ExtractedEventSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  title: z.string().min(1).max(200),
  description: z.string().max(1000).nullable().optional(),
  kind: z.string().max(40).default('event'),
});
export type ExtractedEvent = z.infer<typeof ExtractedEventSchema>;

export const ExtractedEventsSchema = z.object({
  events: z.array(ExtractedEventSchema).max(200),
});
export type ExtractedEvents = z.infer<typeof ExtractedEventsSchema>;
